import { useCallback, useMemo } from "react";
import type { Moment } from "moment";
import type { IAssignment } from "@miplatform/types";
import { Box, Typography } from "@mui/material";
import { AssignmentCard } from "./AssignmentCard";
import {
  AssignmentsUnassignedDisplayMode,
  AssignmentsUnassignedDisplayType,
  DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT,
} from "./AssignmentsCalendar.constants";
import { IAssignmentCalendarUser } from "./AssignmentsCalendar.types";
import { AssignmentsColumn } from "./AssignmentsColumn";
import { AssignmentsUnassignedHeader } from "./AssignmentsUnassignedHeader";
import { AssignmentsUsersRenderer } from "./AssignmentsUsersRenderer";
import { HorizontalScrollContainer } from "./HorizontalScrollContainer";

interface AssignmentsUnassignedContainerProps {
  isCollapsed: boolean;
  date: Moment;
  unassignedAssignments: IAssignment[];
  users: IAssignmentCalendarUser[];
  selectedUser: IAssignmentCalendarUser | null;
  displayType: AssignmentsUnassignedDisplayType;
  displayMode: AssignmentsUnassignedDisplayMode;
  filters: string[];
  filterOptions: { label: string; value: string }[];
  activeTab?: string;
  minHeight?: number;
  onAssignAssignment: (assignmentId: string, assigneeId: string) => void;
  onUnassignAssignment: (assignmentId: string) => void;
  onSelectedUserChange: (user: IAssignmentCalendarUser | null) => void;
  onFiltersChange: (filters: string[]) => void;
  onDisplayTypeChange: (type: AssignmentsUnassignedDisplayType) => void;
  onDisplayModeChange: (mode: AssignmentsUnassignedDisplayMode) => void;
  onCollapseClick: () => void;
}

export const AssignmentsUnassignedContainer = ({
  isCollapsed,
  date,
  unassignedAssignments,
  users,
  activeTab,
  selectedUser,
  displayMode,
  displayType,
  filters,
  filterOptions,
  minHeight,
  onAssignAssignment,
  onUnassignAssignment,
  onSelectedUserChange,
  onFiltersChange,
  onDisplayTypeChange,
  onDisplayModeChange,
  onCollapseClick,
}: AssignmentsUnassignedContainerProps) => {
  const displayTypeHomeKey = useMemo(
    () =>
      displayType === AssignmentsUnassignedDisplayType.City
        ? "cityId"
        : "communityId",
    [displayType]
  );
  const groupLabelsMap = useMemo(
    () =>
      filterOptions.reduce((acc, { value, label }) => {
        acc[value] = label;
        return acc;
      }, {} as Record<string, string>),
    [filterOptions]
  );
  const selectedIds = useMemo(() => {
    if (filters.length === 0) {
      return [
        ...new Set(
          unassignedAssignments.map(
            (assignment) => assignment.home?.[displayTypeHomeKey] || "Unknown"
          )
        ),
      ].sort(
        (a, b) =>
          groupLabelsMap[a]?.localeCompare(groupLabelsMap[b] || "") ||
          a.localeCompare(b)
      );
    }

    return filters.sort(
      (a, b) =>
        groupLabelsMap[a]?.localeCompare(groupLabelsMap[b] || "") ||
        a.localeCompare(b)
    );
  }, [
    filters,
    unassignedAssignments,
    displayType,
    groupLabelsMap,
    displayTypeHomeKey,
  ]);
  const { groupedAssignments } = useMemo(() => {
    const assignmentsGroups = selectedIds.reduce((acc, group) => {
      acc[group] = [];
      return acc;
    }, {} as Record<string, IAssignment[]>);
    unassignedAssignments.forEach((assignment) => {
      const groupKey = assignment.home?.[displayTypeHomeKey] || "Unknown";

      if (!assignmentsGroups[groupKey]) {
        return;
      }

      assignmentsGroups[groupKey].push(assignment);
    });

    const sortedGroups = Object.entries(assignmentsGroups)
      .sort(
        ([aKey], [bKey]) =>
          groupLabelsMap[aKey]?.localeCompare(groupLabelsMap[bKey] || "") ||
          aKey.localeCompare(bKey)
      )
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, IAssignment[]>);

    return {
      assignmentsGroupsMap: sortedGroups,
      groupedAssignments: Object.entries(sortedGroups),
    };
  }, [selectedIds, unassignedAssignments, groupLabelsMap, displayType]);
  const headers = useMemo(
    () =>
      selectedIds.map((id) => ({
        title: groupLabelsMap[id] || id,
        count: groupedAssignments.find(([key]) => key === id)?.[1].length || 0,
      })),
    [selectedIds, groupLabelsMap, groupedAssignments]
  );
  const userGrid = useMemo(() => {
    const firstHalf: IAssignmentCalendarUser[] = [];
    const secondHalf: IAssignmentCalendarUser[] = [];
    users.forEach((user, index) => {
      if (index % 2 === 0) {
        firstHalf.push(user);
      } else {
        secondHalf.push(user);
      }
    });

    return [firstHalf, secondHalf];
  }, [users]);
  const unassignedAssignmentsResetKey = `${displayType}-${displayMode}-${
    activeTab || ""
  }-${groupedAssignments.length}`;

  const handleAssign = useCallback(
    (assignmentId: string) => {
      if (!selectedUser?.id) return;

      onAssignAssignment?.(assignmentId, selectedUser.id);
    },
    [onAssignAssignment, selectedUser?.id]
  );

  const renderEmptyContainer = (text: string) => (
    <Box
      sx={(theme) => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minHeight: minHeight || 260,
        backgroundColor: theme.palette.primary.light,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontSize: theme.fontSize.medium,
          fontWeight: theme.fontWeight.semiBold,
        })}
      >
        {text}
      </Typography>
    </Box>
  );

  const renderUnassignedAssignment = (assignment: IAssignment) => (
    <AssignmentCard
      key={assignment.id}
      assignment={assignment}
      isAssignDisabled={!selectedUser}
      isDisabled={Boolean(
        selectedUser &&
          !selectedUser.assignableAssignmentTemplateIds.includes(
            assignment.assignmentTemplateId || ""
          )
      )}
      onAssign={handleAssign}
    />
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      minWidth={0}
      height={isCollapsed ? "fit-content" : "100%"}
    >
      <AssignmentsUnassignedHeader
        isCollapsed={isCollapsed}
        total={unassignedAssignments.length}
        displayMode={displayMode}
        displayType={displayType}
        filters={filters}
        filterOptions={filterOptions}
        onFiltersChange={onFiltersChange}
        onDisplayTypeChange={onDisplayTypeChange}
        onDisplayModeChange={onDisplayModeChange}
        onCollapseClick={onCollapseClick}
      />
      <Box
        display="flex"
        minWidth={0}
        flex={1}
        sx={{
          height: isCollapsed ? "fit-content" : "100%",
          ...(displayMode !== AssignmentsUnassignedDisplayMode.All && {
            flexDirection: "row",
            gap: 1,
          }),
        }}
      >
        {displayMode !== AssignmentsUnassignedDisplayMode.All && (
          <Box
            sx={{
              position: "relative",
              width:
                displayMode === AssignmentsUnassignedDisplayMode.One
                  ? DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT - 4
                  : DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT * 2 - 4,
              height: "100%",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              <HorizontalScrollContainer
                disableScroll
                headerArrows
                reset={unassignedAssignmentsResetKey}
                scrollX={
                  displayMode === AssignmentsUnassignedDisplayMode.One
                    ? DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT
                    : DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT * 2
                }
                sx={{
                  width: "100%",
                  height: "100%",
                  gap: 1,
                }}
                scrollContainerSx={{
                  gap: 1,
                  height: "100%",
                }}
                headerPerPage={
                  displayMode === AssignmentsUnassignedDisplayMode.One ? 1 : 2
                }
                headers={headers}
              >
                {groupedAssignments.map(([key, value]) => (
                  <AssignmentsColumn
                    isOrdersColumn
                    key={key}
                    minHeight={minHeight}
                    isEmpty={value.length === 0}
                    sx={{ minWidth: 195, maxWidth: 195 }}
                  >
                    {value.map(renderUnassignedAssignment)}
                  </AssignmentsColumn>
                ))}
              </HorizontalScrollContainer>
            </Box>
          </Box>
        )}
        {!isCollapsed && (
          <HorizontalScrollContainer
            disableScroll
            reset={unassignedAssignmentsResetKey}
            sx={{ height: "100%", gap: 1, flex: 1 }}
            scrollX={DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT}
            scrollContainerSx={{
              gap: 1,
              ...(displayMode !== AssignmentsUnassignedDisplayMode.All && {
                flexDirection: "column",
              }),
            }}
          >
            {displayMode === AssignmentsUnassignedDisplayMode.All ? (
              <>
                {groupedAssignments.map(([key, value]) => {
                  const groupLabel = groupLabelsMap[key] || key;
                  const title = groupLabel.includes(",")
                    ? groupLabel.split(",")[0]
                    : groupLabel;
                  const subtitle = groupLabel.includes(",")
                    ? groupLabel.split(",")[1]
                    : "";

                  return (
                    <AssignmentsColumn
                      isOrdersColumn
                      key={key}
                      title={title}
                      subtitle={subtitle}
                      isEmpty={value.length === 0}
                      count={value.length}
                      minHeight={minHeight}
                      sx={{ minWidth: 195, maxWidth: 195 }}
                    >
                      {value.map(renderUnassignedAssignment)}
                    </AssignmentsColumn>
                  );
                })}
                {groupedAssignments.length === 0 &&
                  renderEmptyContainer("No Orders Available")}
              </>
            ) : (
              <>
                {userGrid.map((columnGroup, index) => (
                  <Box display="flex" gap={1} flex={1} key={index}>
                    <AssignmentsUsersRenderer
                      date={date}
                      users={columnGroup}
                      selectedUser={selectedUser}
                      minHeight={minHeight}
                      onSelectedUserChange={onSelectedUserChange}
                      onUnassignAssignment={onUnassignAssignment}
                    />
                  </Box>
                ))}
                {userGrid[0]?.length === 0 &&
                  userGrid[1]?.length === 0 &&
                  renderEmptyContainer("No Users Available")}
              </>
            )}
          </HorizontalScrollContainer>
        )}
      </Box>
    </Box>
  );
};
